import { useState } from 'react';
import { Check, Crown, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { SWIM_PRICING } from '../data/swimLevels';

/**
 * Interactive Swim Lesson Pricing widget — Member vs Non-Member toggle,
 * per-package 4-week / 5-week breakdown.
 */
const SwimPricing = () => {
  const [memberType, setMemberType] = useState('member'); // 'member' | 'nonMember'
  const tier = SWIM_PRICING[memberType];

  const cards = [
    { key: 'group',       data: tier.group,       desc: 'Class size varies by level · best value · all skill levels' },
    { key: 'semiPrivate', data: tier.semiPrivate, desc: '2 students · price shown is total · split per family' },
    { key: 'private',     data: tier.private,     desc: 'One-on-one with instructor · fastest progression' },
    { key: 'largeGroup',  data: tier.largeGroup,  desc: '45-minute competitive conditioning · ages 15 & under' },
  ];

  return (
    <div className="space-y-8" data-testid="swim-pricing-widget">
      {/* Toggle */}
      <div className="flex justify-center">
        <div className="dv-family__price-toggle">
          <button
            type="button"
            onClick={() => setMemberType('member')}
            className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all ${
              memberType === 'member' ? 'bg-[#0072BB] text-white shadow-md' : 'text-gray-600 hover:text-gray-900'
            }`}
            aria-pressed={memberType === 'member'}
            data-testid="pricing-toggle-member"
          >
            <Crown size={14} className="inline mr-1.5 -mt-0.5" />
            Member Pricing
          </button>
          <button
            type="button"
            onClick={() => setMemberType('nonMember')}
            className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all ${
              memberType === 'nonMember' ? 'bg-gray-900 text-white shadow-md' : 'text-gray-600 hover:text-gray-900'
            }`}
            aria-pressed={memberType === 'nonMember'}
            data-testid="pricing-toggle-nonmember"
          >
            Non-Member
          </button>
        </div>
      </div>

      {/* Pricing notice */}
      <div className="text-center text-sm text-gray-600 bg-amber-50 border border-amber-200 rounded-xl py-3 px-5 max-w-2xl mx-auto" data-testid="pricing-notice">
        <Sparkles size={14} className="inline mr-1.5 text-amber-600" />
        <strong>Pricing varies by month</strong> — some months have 4 weeks, others have 5. The 5-week price applies automatically.
      </div>

      {/* Pricing cards */}
      <div className="grid sm:grid-cols-2 gap-5">
        {cards.map(({ key, data, desc }) => {
          const isLargeGroup = key === 'largeGroup';
          return (
            <Card
              key={key}
              className="dv-family__panel"
              data-testid={`swim-pricing-card-${key}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="dv-size-subheading text-3xl mb-1">{data.emoji}</div>
                    <h3 className="dv-size-subheading text-xl font-bold text-gray-900">{data.name}</h3>
                  </div>
                  {key === 'group' && (
                    <Badge className="bg-[#0072BB] text-white border-0">Most Popular</Badge>
                  )}
                </div>
                <p className="dv-size-body text-sm text-gray-600 mb-4">{desc}</p>

                {isLargeGroup ? (
                  <div>
                    <div className="dv-size-subheading text-4xl font-black text-[#0072BB]">${data.monthly}<span className="text-base text-gray-500 font-normal"> / month</span></div>
                    <div className="text-sm text-gray-600 mt-3">{data.schedule}</div>
                    <p className="dv-size-body mt-4 font-semibold">Tryout and instructor approval required</p>
                    <p className="dv-size-body mt-3">Competitive swim conditioning focused on endurance, stroke refinement, race skills, starts, turns, and all four competitive strokes.</p>
                    <p className="dv-size-body mt-3">Tryouts are scheduled the first Tuesday of each month. Ask the aqua desk for availability and tryout details.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs uppercase tracking-wide text-gray-500 font-bold mb-1">4-Week Month</div>
                      <div className="dv-size-subheading text-3xl font-black text-[#0072BB]">${data.four}</div>
                      {data.perChildFour && (
                        <div className="text-xs text-gray-500 mt-0.5">${data.perChildFour}/child</div>
                      )}
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-gray-500 font-bold mb-1">5-Week Month</div>
                      <div className="dv-size-subheading text-3xl font-black text-[#0072BB]">${data.five}</div>
                      {data.perChildFive && (
                        <div className="text-xs text-gray-500 mt-0.5">${data.perChildFive}/child</div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* What's included */}
      <div className="bg-blue-50/50 border border-blue-100 rounded-2xl p-6" data-testid="pricing-includes">
        <h4 className="dv-size-subheading font-bold mb-3 text-gray-900 flex items-center gap-2">
          <Check size={18} className="text-[#0072BB]" />
          Lesson packages include
        </h4>
        <ul className="grid sm:grid-cols-2 gap-y-2 text-sm text-gray-700">
          <li className="dv-size-body flex gap-2"><Check size={16} className="text-[#0072BB] mt-0.5 shrink-0" />25-minute structured lesson</li>
          <li className="dv-size-body flex gap-2"><Check size={16} className="text-[#0072BB] mt-0.5 shrink-0" />Certified instructor</li>
          <li className="dv-size-body flex gap-2"><Check size={16} className="text-[#0072BB] mt-0.5 shrink-0" />93°F heated lesson pool</li>
          <li className="dv-size-body flex gap-2"><Check size={16} className="text-[#0072BB] mt-0.5 shrink-0" />Up to 2 make-ups/month ($20 fee)</li>
          <li className="dv-size-body flex gap-2"><Check size={16} className="text-[#0072BB] mt-0.5 shrink-0" />Age-appropriate curriculum</li>
          <li className="dv-size-body flex gap-2"><Check size={16} className="text-[#0072BB] mt-0.5 shrink-0" />Brentwood location only</li>
        </ul>
      </div>

      <p className="dv-size-body text-gray-600">Advanced Swim Conditioning is a separate 45-minute program in the Lap Pool.</p>

      {/* CTAs */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
        <a href="#swim-inquiry">
          <Button size="lg" className="w-full sm:w-auto bg-[#0072BB] hover:bg-[#005A94] text-white font-bold h-12 px-8" data-testid="pricing-register-cta">
            Request Swim Information
          </Button>
        </a>
        <a href="tel:+19252402990">
          <Button size="lg" variant="outline" className="w-full sm:w-auto border-[#0072BB] text-[#0072BB] hover:bg-[#0072BB] hover:text-white h-12 px-8 font-semibold" data-testid="pricing-call-cta">
            Call (925) 240-2990
          </Button>
        </a>
      </div>
    </div>
  );
};

export default SwimPricing;
